import type { CivilAdviseWizardDraft } from '../../../types/civilAdviseCaseRequest';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import {
  exceedsMax,
  isTodayOrFutureIsoDate,
  maxCharLimitExceededError,
  todayIsoDateLocal,
} from '../../../utils/caseFieldValidation';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import {
  requiredFieldError,
  validationDateFutureOnly,
  validationDocReasonRequired,
} from '../../../utils/validationI18n';
import type { CivilAdviseWizardStep } from './civilAdviseConstants';
import { draftRequiresOtherDeliveryText } from '../../../utils/deliveryMethod';
import { appendInstituteValidationSpecs } from '../../../utils/instituteTypeValidation';
import { CIVIL_ADVISE_FIELD_LABELS as L } from './civilAdviseFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;

export const MAX_DOC_SKIP_REASON = 255;

export function isHighUrgencyLevel(name: string): boolean {
  return name.trim().toLowerCase().includes('high');
}

export function validateCivilAdviseStep(
  step: CivilAdviseWizardStep,
  draft: CivilAdviseWizardDraft,
  fileCategories: LookupItem[] = [],
  corporateInstitutes: LookupItem[] = [],
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};

  if (step >= 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'civ-fileCategory' },
      { key: 'subFileCategoryId', label: 'Sub File Category', value: draft.subFileCategoryId, elementId: 'civ-subFileCategory' },
      { key: 'description', label: 'Description', value: draft.description, elementId: 'civ-description' },
      { key: 'yourReferenceNumber', label: 'Your Reference Number (External)', value: draft.yourReferenceNumber, elementId: 'civ-yourRef' },
      {
        key: 'hasPreviousAdvice',
        label: L.hasPreviousAdvice,
        value: draft.hasPreviousAdvice,
        elementId: 'civ-previousAdvice',
      },
      {
        key: 'hasConnectedCaseRelevant',
        label: L.connectedCaseRelevant,
        value: draft.hasConnectedCaseRelevant,
        elementId: 'civ-connectedCase',
      },
      {
        key: 'hasConnectedMatters',
        label: L.connectedMatters,
        value: draft.hasConnectedMatters,
        elementId: 'civ-connectedMatters',
      },
    );

    appendInstituteValidationSpecs(
      specs,
      draft,
      draft.fileCategoryId,
      fileCategories,
      'civ',
      corporateInstitutes,
    );

    if (draft.hasPreviousAdvice === 'yes' && draft.previousAdviceFiles.length === 0) {
      extra.previousAdviceFiles = requiredFieldError(L.previousAdviceAttachment);
    }
    if (draft.hasConnectedCaseRelevant === 'yes') {
      specs.push({
        key: 'connectedCaseDetails',
        label: L.connectedCaseDetails,
        value: draft.connectedCaseDetails,
        elementId: 'civ-connectedCaseDetails',
      });
    }
    if (draft.hasConnectedMatters === 'yes') {
      specs.push(
        { key: 'connectedCaseNumber', label: L.connectedCaseNumber, value: draft.connectedCaseNumber, elementId: 'civ-connectedCaseNo' },
      );
      if (draft.connectedAgdFileRefAvailable) {
        specs.push(
          { key: 'connectedAgdFileRef', label: L.connectedAgdFileRef, value: draft.connectedAgdFileRef, elementId: 'civ-connectedAgd' },
        );
      }
    }

    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_REF)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_REF)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseDetails, MAX_DESC)) extra.connectedCaseDetails = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();
    if (exceedsMax(draft.corporateInstituteOther, MAX_REF)) extra.corporateInstituteOther = maxCharLimitExceededError();
  }

  if (step >= 2) {
    specs.push({ key: 'urgencyLevelId', label: 'Urgency Level', value: draft.urgencyLevelId, elementId: 'civ-urgency' });
    if (isHighUrgencyLevel(draft.urgencyLevelName)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'civ-urgencyJustification',
      });
    }
    if (exceedsMax(draft.urgencyJustification, MAX_DESC)) extra.urgencyJustification = maxCharLimitExceededError();
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step >= 3) {
    specs.push({ key: 'deliveryMethodIds', label: 'Delivery Method', value: draft.deliveryMethodIds, elementId: 'civ-delivery' });
    if (draftRequiresOtherDeliveryText(draft)) {
      specs.push({
        key: 'deliveryOther',
        label: 'Other Delivery Method',
        value: draft.deliveryOther,
        elementId: 'civ-deliveryOther',
      });
    }
    if (exceedsMax(draft.deliveryOther, MAX_REF)) extra.deliveryOther = maxCharLimitExceededError();
  }

  if (step >= 4) {
    Object.assign(extra, validateStep4Documents(draft));
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

export function validateStep4Documents(draft: CivilAdviseWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if ((doc.files ?? []).length > 0) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}

export function minDeadlineDateForCivilAdvise(): string {
  return todayIsoDateLocal();
}
